/**
 * Returns whether the current user is an org_admin in the **active** workspace.
 *
 * Initial load:
 *   Calls getUserOrganizations() to build a workspaceId→role map, then reads
 *   the active workspace from localStorage ('revisr_active_workspace').
 *   If localStorage is empty (first visit / cleared storage), falls back to the
 *   first workspace — matching WorkspaceSwitcher's own default logic.
 *
 * Workspace switch:
 *   WorkspaceSwitcher fires 'workspace-changed' ONLY on manual user switch
 *   (not on initial load — firing it on load would trigger MainLayout's key
 *   reset, causing an infinite remount loop). The event handler updates the
 *   active workspace and role immediately from the event payload.
 */
import { useState, useEffect } from 'react';
import { useUser } from '@foundation/contextapi/UserContext';
import { getUserOrganizations } from '@foundation/services/organizationService';
import { getActiveWorkspaceId } from './activeWorkspace';

type ActiveWorkspaceRole = {
  workspaceId: string | null;
  role: string | null;
  isOrgAdmin: boolean;
  isLoading: boolean;
};

type WorkspaceChangedDetail = {
  id: string;
  name: string;
  tenant_type: string;
  role: string;
};

export const useActiveWorkspaceRole = (): ActiveWorkspaceRole => {
  const { user } = useUser();

  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    () => getActiveWorkspaceId()
  );
  const [roleMap, setRoleMap] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  // Handle MANUAL workspace switches (WorkspaceSwitcher fires this only on user action).
  // Do NOT fire this event on initial load — MainLayout remounts all children on
  // every workspace-changed event, which would cause an infinite remount loop.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<WorkspaceChangedDetail>).detail;
      if (!detail?.id) return;
      setActiveWorkspaceId(detail.id);
      setRoleMap(prev => {
        const next = new Map(prev);
        next.set(detail.id, detail.role);
        return next;
      });
    };
    window.addEventListener('workspace-changed', handler);
    return () => window.removeEventListener('workspace-changed', handler);
  }, []);

  // Initial load: fetch all org memberships once when the user is known.
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const fetchMemberships = async () => {
      try {
        const { membership } = await getUserOrganizations();
        if (cancelled) return;

        const map = new Map<string, string>();
        membership.forEach(m => {
          map.set(m.organisation_id, m.role);
        });
        setRoleMap(map);

        // If localStorage was empty, fall back to the first workspace —
        // exactly what WorkspaceSwitcher defaults to (saved || items[0]).
        setActiveWorkspaceId(prev => {
          if (prev) return prev;
          const firstId = membership[0]?.organisation_id ?? null;
          if (firstId) localStorage.setItem('revisr_active_workspace', firstId);
          return firstId;
        });
      } catch (err) {
        console.error('[useActiveWorkspaceRole] Failed to fetch memberships:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchMemberships();
    return () => { cancelled = true; };
  }, [user?.id]);

  const role = activeWorkspaceId ? (roleMap.get(activeWorkspaceId) ?? null) : null;

  // organizationService maps db role 'org_admin' -> 'admin'. Guard both spellings.
  const isOrgAdmin = role === 'admin' || role === 'org_admin';

  return { workspaceId: activeWorkspaceId, role, isOrgAdmin, isLoading };
};
